/**
 * MerakiPostSignoffPage — runs the "leaving an install" automation against
 * a single network. The legacy Django page does four things on the device:
 *
 *   1. Remove VLAN 1
 *   2. Set switch management VLAN to 700
 *   3. Block DHCP servers except for the Meraki firewall MAC
 *   4. Disable all unused ports
 *
 * UX flow: pick a network → review the steps → click Execute → confirmation
 * dialog (this is destructive on a live network) → progress dialog polls
 * celery → success or failure toast.
 */
import { useEffect, useMemo, useReducer, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  ListChecks,
  PlayCircle,
  ShieldAlert,
  XOctagon,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { NetworkPicker } from "@/components/app/network-picker";
import { MerakiSubNav } from "@/components/app/meraki-sub-nav";
import { NoVendorKeysBanner } from "@/components/app/no-vendor-keys-banner";
import { post as apiPost, ApiError } from "@/api/client";
import { pollCeleryTask } from "@/api/celery";
import { CeleryTaskSchema, type MerakiNetwork } from "@/api/schemas/meraki";
import { useMerakiKeys } from "@/stores/meraki-keys";

/**
 * Static description of what the post-signoff routine actually does.
 * Matches the help-tooltip copy from the legacy Django template verbatim.
 */
const SIGNOFF_STEPS: Array<{ title: string; detail: string }> = [
  { title: "Remove VLAN 1", detail: "Drops the default Meraki VLAN to prevent rogue devices from joining the management plane." },
  { title: "Set switch management VLAN to 700", detail: "Migrates all switch SVIs to the GuestTek-standard management VLAN." },
  { title: "Block external DHCP servers", detail: "Whitelists only the Meraki firewall MAC; rogue DHCP gets dropped at ingress." },
  { title: "Disable unused ports", detail: "Administratively shuts every port that hasn't seen a link in the last 24h." },
];

export function MerakiPostSignoffPage() {
  const [network, setNetwork] = useState<MerakiNetwork | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);

  type SyncState = { syncOpen: boolean; syncPct: number | undefined; syncMessage: string; syncDone: boolean };
  const [{ syncOpen, syncPct, syncMessage, syncDone }, setSync] = useReducer(
    (s: SyncState, p: Partial<SyncState>) => ({ ...s, ...p }),
    { syncOpen: false, syncPct: undefined, syncMessage: "Queueing sign-off task…", syncDone: false },
  );

  const { data: keys, load: loadKeys } = useMerakiKeys();
  useEffect(() => {
    void loadKeys();
  }, [loadKeys]);
  const activeKeyId = useMemo(() => {
    const active = keys.find((k) => k.active);
    return (active ?? keys[0])?.id ?? null;
  }, [keys]);

  /**
   * Hit the legacy `/api/meraki/execute_signoff/` endpoint and then drive
   * the progress modal off the returned celery task id.
   */
  async function runSignoff() {
    if (!network) return;
    setConfirmOpen(false);
    setSync({ syncOpen: true, syncDone: false, syncPct: undefined, syncMessage: "Queueing sign-off task…" });
    try {
      const raw = await apiPost<unknown>("/meraki/execute_signoff/", {
        networkId: network.id,
        merakiKeyId: activeKeyId,
      });
      const parsed = CeleryTaskSchema.safeParse(raw);
      if (!parsed.success) {
        toast.success("Sign-off queued", {
          description: "Could not attach to the progress channel; check the audit log for the result.",
        });
        setSync({ syncOpen: false });
        return;
      }
      setSync({ syncMessage: "Reading current configuration…" });
      const final = await pollCeleryTask(parsed.data.taskId, (p) => {
        if (p.total && p.current !== undefined) setSync({ syncPct: (p.current / p.total) * 100 });
        if (p.message) setSync({ syncMessage: p.message });
      });
      if (final.state === "SUCCESS") {
        setSync({ syncDone: true });
        setTimeout(() => setSync({ syncOpen: false }), 1400);
        toast.success("Post sign-off complete", {
          description: `${network.name} has been hardened.`,
        });
      } else {
        toast.error("Post Sign-off failed", {
          description: "Some steps did not apply. Check the audit log for details.",
        });
        setSync({ syncOpen: false });
      }
    } catch (err) {
      const msg =
        err instanceof ApiError ? `API error ${err.status}` : err instanceof Error ? err.message : "unknown error";
      toast.error("Could not run post sign-off", { description: msg });
      setSync({ syncOpen: false });
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Crumb */}
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">Operations</Link>
        <span className="opacity-40">/</span>
        <Link to="/meraki/provisioning" className="text-fg-dim hover:text-fg">Meraki</Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">Post Sign-off</span>
      </div>

      {/* Vendor sub-nav */}
      <MerakiSubNav />

      <NoVendorKeysBanner vendor="meraki" />

      {/* Hero */}
      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">Post Sign-off</h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            Runs the GuestTek hardening sequence on a network before leaving an install — VLAN 1 removal, mgmt VLAN
            migration, rogue-DHCP block and unused-port shutdown.
          </p>
        </div>
        <Badge variant="warn" size="md">
          <ShieldAlert size={11} />
          Destructive — touches live config
        </Badge>
      </div>

      {/* Two-column body — picker on the left, step description on the right. */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[420px_1fr]">

        {/* Left — network picker + execute button */}
        <Card>
          <CardContent className="flex flex-col gap-4">
            <div>
              <CardTitle>Network</CardTitle>
              <CardSub className="mt-1">
                Pick the network to harden. The active Meraki key will be used.
              </CardSub>
            </div>
            <NetworkPicker value={network} onChange={setNetwork} />
            <Button
              size="lg"
              onClick={() => setConfirmOpen(true)}
              disabled={!network}
            >
              <PlayCircle size={16} />
              Execute sign-off
            </Button>
            {!network ? (
              <p className="m-0 text-[11px] text-fg-mute">Select a network to enable execute.</p>
            ) : (
              <div className="flex items-start gap-2 rounded-lg border border-peach bg-peach/10 p-3 text-xs text-peach">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>
                  This is destructive on a live network — disabled ports will drop links.
                  Run in a maintenance window unless cleaning up after a cutover.
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right — step list (the help tooltip copy from the legacy template) */}
        <Card>
          <CardContent>
            <div className="mb-3 flex items-center gap-2">
              <ListChecks size={16} className="text-violet" />
              <CardTitle>What runs, in order</CardTitle>
            </div>
            <ol className="m-0 flex flex-col gap-3 p-0">
              {SIGNOFF_STEPS.map((s, i) => (
                <li
                  key={s.title}
                  className="flex items-start gap-3 rounded-xl border border-line bg-surface p-3"
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-aurora-soft font-mono text-xs font-bold text-violet">
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-fg">{s.title}</div>
                    <div className="mt-0.5 text-xs text-fg-dim">{s.detail}</div>
                  </div>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      </div>

      {/* Confirmation dialog — explicit OK to reduce accidental fire. */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogTitle>Run post sign-off?</DialogTitle>
          <DialogDescription className="mt-2">
            <span>You are about to run the four-step hardening on:</span>
            <span className="mt-2 block rounded-lg border border-line bg-surface px-3 py-2 font-mono text-sm text-fg">
              {network?.name ?? "—"}
            </span>
            <span className="mt-3 block">
              Unused ports will be administratively disabled. This typically takes 30–60 seconds and is reversible
              from the Meraki dashboard.
            </span>
          </DialogDescription>
          <div className="mt-5 flex justify-end gap-2.5">
            <DialogClose asChild>
              <Button variant="ghost">Cancel</Button>
            </DialogClose>
            <Button onClick={() => void runSignoff()}>
              <PlayCircle size={14} />
              Confirm &amp; run
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Progress dialog */}
      <Dialog open={syncOpen} onOpenChange={(v) => setSync({ syncOpen: v })}>
        <DialogContent>
          {syncDone ? (
            <div className="flex flex-col items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-mint/15 text-mint">
                <CheckCircle2 size={22} />
              </div>
              <DialogTitle>Sign-off complete</DialogTitle>
              <DialogDescription>
                {network?.name ?? "Network"} has been hardened. Closing in a moment…
              </DialogDescription>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <DialogTitle>Running sign-off · {network?.name ?? "network"}</DialogTitle>
              <DialogDescription>{syncMessage}</DialogDescription>
              <Progress value={syncPct} hint={syncPct ? undefined : "Establishing connection…"} />
              <div className="flex items-start gap-2 rounded-lg border border-line bg-surface p-3 text-xs text-fg-dim">
                <XOctagon size={14} className="mt-0.5 shrink-0 text-rose" />
                <span>
                  Closing this dialog will not cancel the sign-off — it continues running on the server. Use the audit
                  log to follow up.
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
